import React, { useCallback } from 'react';
import {
  Box,
  Checkbox,
  ChevronRightIcon,
  Inscription,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import REGEXP from '@/common/regexp';
import { formatCategoryType } from '@/utils';

import {
  StyledCheckboxNoMargin,
} from './SyncUpdatesModal.styles';
import { PropertyChangeItem } from './SyncUpdatesModal.types';
import { SyncUpdatesPropertyWarningBanner } from './SyncUpdatesPropertyWarningBanner';

type FormattedValue = { type: 'list'; items: string[] } | { type: 'text'; value: string };

const toText = (value: string): FormattedValue => ({ type: 'text', value });
const EMPTY: FormattedValue = { type: 'text', value: '—' };

const formatItem = (item: unknown): string => {
  if (typeof item === 'string') return formatCategoryType(item);
  if (item && typeof item === 'object') {
    return Object.values(item)
      .filter((v): v is string | number => typeof v === 'string' || typeof v === 'number')
      .map((v) => typeof v === 'string' ? formatCategoryType(v) : `$${Number(v).toFixed(2)}`)
      .join(', ');
  }
  return '';
};

const formatValue = (value: string | null): FormattedValue => {
  if (!value || value === 'null') return EMPTY;

  if (value.includes('T') && dayjs(value).isValid()) {
    return toText(dayjs(value).format(DEFAULT_DATE_FORMAT));
  }

  if (value.startsWith('[') && value.endsWith(']')) {
    try {
      const parsed = JSON.parse(value);
      const items = Array.isArray(parsed) ? parsed.map(formatItem).filter(Boolean) : [];
      return items.length ? { type: 'list', items } : EMPTY;
    } catch {
      const items = value.slice(1, -1).split(',').map((s) => formatCategoryType(s.trim())).filter(Boolean);
      return items.length ? { type: 'list', items } : EMPTY;
    }
  }

  if (REGEXP.UPPER_SNAKE_CASE.test(value)) return toText(formatCategoryType(value));
  return toText(value);
};

const renderValue = (formatted: FormattedValue, propertyName: string, side: 'previous' | 'updated') => {
  if (formatted.type === 'list') {
    return (
      <Box
        direction="column"
        data-testid={`sync-updates-modal_value-list_${propertyName}_${side}`}
      >
        {formatted.items.map((item, index) => (
          <Inscription key={`${item}-${index}`} style={{ wordBreak: 'break-all' }}>{item}</Inscription>
        ))}
      </Box>
    );
  }
  return <Inscription data-testid={`sync-updates-modal_value-text_${propertyName}_${side}`} style={{ wordBreak: 'break-all' }}>{formatted.value}</Inscription>;
};

interface Props {
  item: PropertyChangeItem;
  index: number;
  isSelected: boolean;
  isRootSelected?: boolean;
  onToggleProperty: (propertyName: string) => void;
  showCheckbox?: boolean;
}

const SyncUpdatesPropertyRow: React.FC<Props> = ({
  item,
  index,
  isSelected,
  isRootSelected = false,
  onToggleProperty,
  showCheckbox = true,
}) => {
  const handleChange = useCallback(() => {
    onToggleProperty(item.propertyName);
  }, [onToggleProperty, item.propertyName]);

  const oldFormatted = formatValue(item.oldValue ?? null);
  const newFormatted = formatValue(item.newValue ?? null);
  return (
    <Box
      direction="column"
      pad={{ horizontal: 'spacing12' }}
      data-testid={`sync-updates-modal_property_${item.propertyName}`}
    >
      {index > 0 && <Box height="1px" background="border1" margin={{ vertical: 'xs' }} />}
      <Box direction="column" gap="spacing8">
        <Box direction="row" align="center" gap="spacing8">
          {showCheckbox && (
            <StyledCheckboxNoMargin>
              <Checkbox
                data-testid={`sync-updates-modal_property-checkbox_${item.propertyName}`}
                checked={isSelected}
                onChange={handleChange}
              />
            </StyledCheckboxNoMargin>
          )}
          <Box flex direction="column">
            <Inscription weight="bold" color="textTitle" data-testid={`sync-updates-modal_property-name_${item.propertyName}`}>
              {item.displayName}
            </Inscription>
            {item.description && (
              <Inscription size="small" color="textSecondary">
                {item.description}
              </Inscription>
            )}
          </Box>
        </Box>
        <Box direction="row" align="start">
          <Box flex direction="column" align="start">
            <Inscription size="small" color="textSecondary">Previous</Inscription>
            {renderValue(oldFormatted, item.propertyName, 'previous')}
          </Box>
          <Box flex direction="column" align="start">
            <Box direction="row" align="center" gap="spacing4">
              <ChevronRightIcon color="iconSecondary" size="8px" />
              <Inscription size="small" color="textSecondary">Updated</Inscription>
            </Box>
            <Box margin={{ left: 'spacing8' }}>
              {renderValue(newFormatted, item.propertyName, 'updated')}
            </Box>
          </Box>
        </Box>
        {item.hasDependencyWarning && !isRootSelected && (
          // <WarningCard
          //   data-testid={`sync-updates-modal_warning_${item.propertyName}`}
          //   borderColor="warningBorder"
          //   containerProps={{ pad: { vertical: 'spacing12', horizontal: 'spacing16' } }}
          //   title={<Inscription size="small" color="textBody">{SYNC_UPDATES_WARNING_TEXT}</Inscription>}
          // />
          <Box data-testid={`sync-updates-modal_warning_${item.propertyName}`}>
            <SyncUpdatesPropertyWarningBanner />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SyncUpdatesPropertyRow;
